import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import {
    Button,
    ButtonRow,
    Card,
    Checkbox,
    FormErrorMessage,
    FormGroup,
    Heading,
    Radio,
    RadioGroup,
    Text,
    TextArea,
} from '@ksyos/design-system';
import { useState } from 'react';
import { styled } from 'styled-components';
import type { IDermaOthProcedureCustomControlData } from '../../../../../../common/validation/customControls/dermatology';
import type { IAltoStepProps } from '../../NewAltoDermaOthProcedure';
import type { IRegularStepProps } from '../../NewDermaOthProcedure';
import {
    conditionChangedOptions,
    conditionColorChangedOptions,
    conditionSizeChangedOptions,
    conditionStartedOptions,
    yesNoOptions,
} from '../../../../../../common/validation/customControls/dermatology';
import { translateKey } from '../../../../../translate';
import { CustomContainer } from '../../../../elements/molecules/customContainer/CustomContainer';
import { Stack } from '../../../../elements/molecules/stack/Stack';
import { PageIntro } from '../../../../elements/organisms/pageIntro/PageIntro';

export function Step4(props: (IRegularStepProps | IAltoStepProps) & { altoFlow?: boolean }) {
    const [andersChecked, setAndersChecked] = useState(!!props.procedure.customControlData.condition.anders);
    const altoPatient = 'csPatientName' in props ? props.csPatientName : undefined;

    function showRadioGroup(
        options: string[],
        customControlKey: keyof IDermaOthProcedureCustomControlData
    ): ReactElement {
        return (
            <>
                <StyledCard error={props.errors.has(customControlKey)}>
                    <RadioGroupWrapper>
                        <RadioGroup
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                props.handleChange({ [customControlKey]: event.target.value })
                            }
                            value={(props.procedure.customControlData[customControlKey] as string) ?? ''}
                        >
                            {options.map((option) => (
                                <Radio
                                    id={customControlKey + option}
                                    key={customControlKey + option}
                                    label={option}
                                    value={option}
                                />
                            ))}
                        </RadioGroup>
                    </RadioGroupWrapper>
                </StyledCard>
                {props.errors.has(customControlKey) && (
                    <FormErrorMessage>
                        {translateKey('validation.' + props.errors.get(customControlKey))}
                    </FormErrorMessage>
                )}
            </>
        );
    }

    function jaNeeElements(
        customControlKey: keyof IDermaOthProcedureCustomControlData,
        customControlKeyRemark: keyof IDermaOthProcedureCustomControlData,
        title: string,
        placeholder: string,
        thirdOption: string
    ): ReactElement {
        const options = [...yesNoOptions, thirdOption];

        return (
            <>
                <Stack>
                    <StyledCard error={props.errors.has(customControlKey)}>
                        <RadioGroupWrapper>
                            <RadioGroup
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const updatedCustomControlData = { ...props.procedure.customControlData };
                                    (updatedCustomControlData[customControlKey] as string) = event.target.value;
                                    if (event.target.value !== 'Ja') {
                                        (updatedCustomControlData[customControlKeyRemark] as null) = null;
                                    }
                                    props.handleChange(updatedCustomControlData);
                                }}
                                value={(props.procedure.customControlData[customControlKey] as string) ?? ''}
                            >
                                {options.map((option) => (
                                    <Radio
                                        id={customControlKey + option}
                                        key={customControlKey + option}
                                        label={option}
                                        value={option}
                                    />
                                ))}
                            </RadioGroup>
                        </RadioGroupWrapper>
                    </StyledCard>
                    {props.errors.has(customControlKey) && (
                        <FormErrorMessage>
                            {translateKey('validation.' + props.errors.get(customControlKey))}
                        </FormErrorMessage>
                    )}
                </Stack>
                {props.procedure.customControlData[customControlKey] === 'Ja' && (
                    <Stack>
                        <Heading
                            mb={4}
                            size="small"
                        >
                            {title}
                        </Heading>
                        <TextArea
                            autoResize
                            error={
                                props.errors.get(customControlKeyRemark)
                                    ? translateKey('validation.' + props.errors.get(customControlKeyRemark))
                                    : undefined
                            }
                            id={customControlKeyRemark}
                            onChange={(e) => {
                                const remark = e.target.value !== '' ? e.target.value : null;
                                props.handleChange({ [customControlKeyRemark]: remark });
                            }}
                            placeholder={placeholder}
                            value={(props.procedure.customControlData[customControlKeyRemark] as string | null) || ''}
                        />
                    </Stack>
                )}
            </>
        );
    }

    return (
        <>
            <PageIntro
                subtitle="Je bent er bijna! We stellen je graag nog een paar vragen."
                title="Vragen"
            />
            <CustomContainer>
                <Stack>
                    <Step>1/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('condition')}
                        label="Hoe voelt de aandoening?"
                        layout="vertical"
                    >
                        <StyledCard error={props.errors.has('condition')}>
                            <CheckboxWrapper>
                                <Checkbox
                                    checked={props.procedure.customControlData.condition.jeukt}
                                    label="Het jeukt"
                                    onChange={() => {
                                        const condition = { ...props.procedure.customControlData.condition };
                                        condition.jeukt = !condition.jeukt;
                                        props.handleChange({ condition });
                                    }}
                                />
                            </CheckboxWrapper>
                            <CheckboxWrapper>
                                <Checkbox
                                    checked={props.procedure.customControlData.condition.pijn}
                                    label="Het doet pijn"
                                    onChange={() => {
                                        const condition = { ...props.procedure.customControlData.condition };
                                        condition.pijn = !condition.pijn;
                                        props.handleChange({ condition });
                                    }}
                                />
                            </CheckboxWrapper>
                            <CheckboxWrapper>
                                <Checkbox
                                    checked={andersChecked}
                                    label="Anders, namelijk..."
                                    onChange={(e) => {
                                        const checked = (e.target as HTMLInputElement).checked;
                                        setAndersChecked(checked);
                                        if (!checked) {
                                            props.handleChange({
                                                condition: {
                                                    ...props.procedure.customControlData.condition,
                                                    anders: null,
                                                },
                                            });
                                        }
                                    }}
                                />
                            </CheckboxWrapper>
                        </StyledCard>
                        {props.errors.has('condition') && (
                            <FormErrorMessage>
                                {translateKey('validation.' + props.errors.get('condition'))}
                            </FormErrorMessage>
                        )}
                    </StyledFormGroup>
                </Stack>
                {andersChecked && (
                    <Stack>
                        <Heading
                            mb={4}
                            size="small"
                        >
                            Typ je opmerking...
                        </Heading>
                        <TextArea
                            autoResize
                            error={
                                props.errors.get('condition.anders')
                                    ? translateKey('validation.' + props.errors.get('condition.anders'))
                                    : undefined
                            }
                            onChange={(e) => {
                                const anders = e.target.value !== '' ? e.target.value : null;
                                props.handleChange({
                                    condition: { ...props.procedure.customControlData.condition, anders },
                                });
                            }}
                            placeholder="Anders, namelijk..."
                            value={props.procedure.customControlData.condition.anders || ''}
                        />
                    </Stack>
                )}

                <Stack>
                    <Step>2/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('conditionStarted')}
                        label="Wanneer is het begonnen?"
                        layout="vertical"
                    >
                        {showRadioGroup(conditionStartedOptions, 'conditionStarted')}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>3/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('conditionChanged')}
                        label="Is het ondertussen veranderd?"
                        layout="vertical"
                    >
                        {showRadioGroup(conditionChangedOptions, 'conditionChanged')}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>4/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('conditionColorChanged')}
                        label="Is de kleur veranderd?"
                        layout="vertical"
                    >
                        {showRadioGroup(conditionColorChangedOptions, 'conditionColorChanged')}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>5/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('conditionSizeChanged')}
                        label="Is de grootte veranderd?"
                        layout="vertical"
                    >
                        {showRadioGroup(conditionSizeChangedOptions, 'conditionSizeChanged')}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>6/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('conditionBefore')}
                        label={`${
                            props.altoFlow && altoPatient ? `Heeft ${altoPatient}` : 'Heb je'
                        } deze aandoening eerder gehad?`}
                        layout="vertical"
                    >
                        {jaNeeElements(
                            'conditionBefore',
                            'conditionBeforeRemark',
                            'Wanneer en wat is er toen aan gedaan?',
                            ' Typ je opmerking...',
                            'Weet ik niet zeker'
                        )}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>7/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('treatingCondition')}
                        label={
                            props.altoFlow ? 'Is er al een behandeling geprobeerd?' : 'Behandel je de aandoening zelf?'
                        }
                        layout="vertical"
                    >
                        {jaNeeElements(
                            'treatingCondition',
                            'treatingConditionRemark',
                            `Hoe ${props.altoFlow ? 'wordt de aandoening behandeld' : 'behandel je de aandoening'}?`,
                            ' Typ je opmerking...',
                            'Weet ik niet'
                        )}
                    </StyledFormGroup>
                </Stack>

                <Stack>
                    <Step>8/8</Step>
                    <StyledFormGroup
                        hasErrors={props.errors.has('extraQuestion')}
                        label="Heb je een vraag of opmerking aan de arts?"
                        layout="vertical"
                    >
                        <TextArea
                            autoResize
                            error={
                                props.errors.get('extraQuestion')
                                    ? translateKey('validation.' + props.errors.get('extraQuestion'))
                                    : undefined
                            }
                            onChange={(e) => {
                                const extraQuestion = e.target.value !== '' ? e.target.value : null;
                                props.handleChange({ extraQuestion });
                            }}
                            placeholder="Typ je vraag of opmerking..."
                            value={props.procedure.customControlData.extraQuestion || ''}
                        />
                    </StyledFormGroup>
                </Stack>

                <ButtonRow justify="right">
                    <Button
                        color="secondary"
                        onClick={props.validateAndGoToNextStep}
                        variant="solid"
                        width="full-below-small"
                    >
                        Verder
                    </Button>
                </ButtonRow>
            </CustomContainer>
        </>
    );
}

const StyledFormGroup = styled(FormGroup)`
    & > div {
        max-width: 100%;
    }
    & > div > label {
        margin: 0;
        font-size: 19px;
        line-height: 28px;
        margin-bottom: 16px;
    }
`;

function Step({ children }: { children: ReactNode }) {
    return (
        <Text
            color="secondary"
            mb={2}
            size="sm"
        >
            {children}
        </Text>
    );
}

export interface IStyledCardProps {
    error?: boolean;
}

const StyledCard = styled(Card)<IStyledCardProps>`
    border-radius: 3px;
    border: ${(props) => (props.error ? '1px solid #FF9A1E' : 'none')};
`;

const CheckboxWrapper = styled.div`
    padding-left: 16px;

    &:not(:last-child) {
        border-bottom: 1px solid #ecece6;
    }
    label {
        font-size: 16px;
        padding: 21px 12px;
        width: 100%;
    }
`;

const RadioGroupWrapper = styled.div`
    & > div {
        display: block;
    }
    & > div > div {
        padding-left: 16px;
        margin: 0;

        &:not(:last-child) {
            border-bottom: 1px solid #ecece6;
        }
        label {
            font-size: 16px;
            padding: 21px 12px;
            width: 100%;
        }
    }
`;
